/**
 * 批量 IP 化抽屉
 * - 列出所有尚未 IP 化的热点(没有 ipSeed.ipName)
 * - 支持勾选 + 单条 IP 化 + 批量 IP 化所选 / 全选
 */
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Wand2, X, Flame } from 'lucide-react';
import type { TopicCard } from '@starfox/shared-types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  topics: TopicCard[];
  ipizingIds: Set<string>;
  onClose: () => void;
  onIpize: (ids: string[]) => void;
}

export function IpizeDrawer({ open, topics, ipizingIds, onClose, onIpize }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const allChecked = useMemo(
    () => topics.length > 0 && topics.every((t) => selected.has(t.topicId)),
    [topics, selected],
  );

  if (!open) return null;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allChecked ? new Set() : new Set(topics.map((t) => t.topicId)));

  const batchSelected = () => {
    onIpize([...selected]);
    setSelected(new Set());
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex">
      <button
        type="button"
        aria-label="close"
        className="flex-1 cursor-default bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <aside className="flex h-full w-full max-w-xl flex-col overflow-hidden bg-surface shadow-m3">
        <header className="flex items-center justify-between gap-3 border-b border-outline-variant p-4">
          <div>
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-info">
              <Wand2 className="h-3.5 w-3.5" />
              待 IP 化热点
            </div>
            <h2 className="mt-0.5 text-base font-semibold text-neutral-800">
              选择需要 LLM 重新打标 + 生成 IP 包的热点
            </h2>
            <p className="mt-0.5 text-xs text-neutral-500">
              共 {topics.length} 条 · 已选 {selected.size} 条
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="flex items-center justify-between gap-2 border-b border-outline-variant px-4 py-2 text-xs">
          <label className="flex cursor-pointer items-center gap-2 text-neutral-600">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={toggleAll}
              className="h-3.5 w-3.5"
            />
            全选
          </label>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onIpize(topics.map((t) => t.topicId))}
              disabled={topics.length === 0}
              title="对当前列表所有未 IP 化的热点重跑 LLM"
            >
              全部 IP 化
            </Button>
            <Button
              size="sm"
              variant="agent"
              agentColor="radar"
              onClick={batchSelected}
              disabled={selected.size === 0}
            >
              <Wand2 className="h-3.5 w-3.5" />
              IP 化所选 ({selected.size})
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {topics.length === 0 ? (
            <div className="rounded-md border border-dashed border-outline-variant p-8 text-center text-sm text-neutral-500">
              当前主榜所有热点都已 IP 化
            </div>
          ) : (
            <ul className="space-y-2">
              {topics.map((t) => {
                const checked = selected.has(t.topicId);
                const ipizing = ipizingIds.has(t.topicId);
                return (
                  <li
                    key={t.topicId}
                    className={cn(
                      'flex items-center gap-3 rounded-md border border-outline-variant/60 bg-surface-variant/50 px-3 py-2 text-sm',
                      checked && 'border-info bg-info-soft/40',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(t.topicId)}
                      disabled={ipizing}
                      className="h-3.5 w-3.5 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="line-clamp-1 text-sm text-neutral-800">{t.title}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-neutral-500">
                        <span className="inline-flex items-center gap-0.5">
                          <Flame className="h-3 w-3 text-warning" />
                          {(t.dimensions.heat * 100).toFixed(0)}
                        </span>
                        {(t.genres ?? []).slice(0, 2).map((g) => (
                          <Badge key={g} variant="info" className="text-[10px]">
                            {g}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onIpize([t.topicId])}
                      disabled={ipizing}
                    >
                      <Wand2 className={cn('h-3.5 w-3.5', ipizing && 'animate-pulse')} />
                      {ipizing ? '生成中…' : '单条'}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
